import type { TrackConfig } from '../game/types';
import type { TireHealth, WheelId } from '@cars/shared';
import { HudMessageQueue, type HudMessagePriority } from './hudMessageQueue';

export type TimingMessageTone = 'purple' | 'green' | 'red' | 'neutral';

const SPEED_METER_BEGIN_ANGLE = 120;
const SPEED_METER_END_ANGLE = 360;
const NO_TIME = '-.---';
const TIRE_ORDER: readonly WheelId[] = ['frontLeft', 'frontRight', 'rearLeft', 'rearRight'];
const TIRE_LABELS: Record<WheelId, string> = {
  frontLeft: 'FL',
  frontRight: 'FR',
  rearLeft: 'RL',
  rearRight: 'RR',
};

export class HudOverlay {
  private readonly root: HTMLDivElement;
  private readonly currentTimeValue: HTMLSpanElement;
  private readonly lastLapValue: HTMLSpanElement;
  private readonly bestLapValue: HTMLSpanElement;
  private readonly speedValue: HTMLSpanElement;
  private readonly speedArrow: HTMLImageElement;
  private readonly minimapCarPoint: HTMLDivElement;
  private readonly statusMessage: HTMLDivElement;
  private readonly zoneStatus: HTMLDivElement;
  private readonly tireValues: Record<WheelId, HTMLSpanElement>;
  private readonly tireCells: Record<WheelId, HTMLDivElement>;
  private displayedTires: TireHealth | null = null;
  private announcedTireLevels: Record<WheelId, string> = {
    frontLeft: 'healthy',
    frontRight: 'healthy',
    rearLeft: 'healthy',
    rearRight: 'healthy',
  };
  private statusTimerId: number | null = null;
  private readonly messageQueue = new HudMessageQueue();

  private minimapScale = 1;

  constructor(uiLayer: HTMLElement, trackConfig: TrackConfig, trackWidth: number) {
    this.root = document.createElement('div');
    this.root.className = 'hud-overlay';

    const panel = document.createElement('div');
    panel.className = 'hud-panel';

    this.currentTimeValue = document.createElement('span');
    this.lastLapValue = document.createElement('span');
    this.bestLapValue = document.createElement('span');
    this.speedValue = document.createElement('span');

    panel.append(
      this.makeRow('Current', this.currentTimeValue),
      this.makeRow('Last', this.lastLapValue),
      this.makeRow('Best', this.bestLapValue),
      this.makeRow('Speed', this.speedValue),
    );

    const speedometer = document.createElement('div');
    speedometer.className = 'hud-speedometer';

    const speedometerBg = document.createElement('img');
    speedometerBg.src = '/assets/HUD/Speedometer.png';
    speedometerBg.alt = 'Speedometer';
    speedometerBg.className = 'hud-speedometer-bg';

    this.speedArrow = document.createElement('img');
    this.speedArrow.src = '/assets/HUD/Speedometer arrow.png';
    this.speedArrow.alt = 'Speedometer arrow';
    this.speedArrow.className = 'hud-speedometer-arrow';

    speedometer.append(speedometerBg, this.speedArrow);

    const minimap = document.createElement('div');
    minimap.className = 'hud-minimap';

    const minimapImage = document.createElement('img');
    minimapImage.src = trackConfig.minimapPath;
    minimapImage.alt = `Track ${trackConfig.id} minimap`;
    minimapImage.className = 'hud-minimap-image';

    this.minimapCarPoint = document.createElement('div');
    this.minimapCarPoint.className = 'hud-minimap-car-point';

    minimap.append(minimapImage, this.minimapCarPoint);

    const tires = document.createElement('div');
    tires.className = 'hud-tires';
    tires.setAttribute('aria-label', 'Tire health');
    this.tireValues = {} as Record<WheelId, HTMLSpanElement>;
    this.tireCells = {} as Record<WheelId, HTMLDivElement>;
    for (const wheel of TIRE_ORDER) {
      const cell = document.createElement('div');
      cell.className = 'hud-tire';
      const label = document.createElement('span');
      label.className = 'hud-tire-label';
      label.textContent = TIRE_LABELS[wheel];
      const fill = document.createElement('span');
      fill.className = 'hud-tire-fill';
      const value = document.createElement('span');
      value.className = 'hud-tire-value';
      cell.append(label, fill, value);
      tires.appendChild(cell);
      this.tireCells[wheel] = cell;
      this.tireValues[wheel] = value;
    }

    this.statusMessage = document.createElement('div');
    this.statusMessage.className = 'hud-status-message';
    this.statusMessage.setAttribute('aria-live', 'polite');
    this.statusMessage.setAttribute('aria-atomic', 'true');
    this.zoneStatus = document.createElement('div');
    this.zoneStatus.className = 'hud-zone-status';
    this.zoneStatus.setAttribute('aria-live', 'polite');
    this.zoneStatus.setAttribute('aria-label', 'Pit zone status');

    this.root.append(panel, speedometer, minimap, tires, this.zoneStatus, this.statusMessage);
    uiLayer.innerHTML = '';
    uiLayer.appendChild(this.root);

    this.minimapScale = minimapImage.naturalWidth > 0 ? minimapImage.naturalWidth / trackWidth : 1;
    minimapImage.addEventListener('load', () => {
      this.minimapScale = minimapImage.width / trackWidth;
    });

    this.setCurrentTime(0);
    this.setLastTime(0);
    this.setBestTime(0);
    this.setSpeed(0);
    this.setTireHealth({ frontLeft: 100, frontRight: 100, rearLeft: 100, rearRight: 100 });
  }

  setCurrentTime(timeMs: number): void {
    this.currentTimeValue.textContent = this.formatTime(timeMs);
  }

  setLastTime(timeMs: number): void {
    this.lastLapValue.textContent = this.formatTime(timeMs);
  }

  setBestTime(timeMs: number): void {
    this.bestLapValue.textContent = this.formatTime(timeMs);
  }

  setSpeed(speedRatio: number): void {
    const clamped = Math.max(0, Math.min(1, speedRatio));
    const angle =
      SPEED_METER_BEGIN_ANGLE + (SPEED_METER_END_ANGLE - SPEED_METER_BEGIN_ANGLE) * clamped;
    this.speedValue.textContent = clamped.toFixed(2);
    this.speedArrow.style.transform = `translate(-50%, -88%) rotate(${angle}deg)`;
  }

  setTireHealth(tires: TireHealth): void {
    const displayed: TireHealth = {
      frontLeft: clampTirePercent(tires.frontLeft),
      frontRight: clampTirePercent(tires.frontRight),
      rearLeft: clampTirePercent(tires.rearLeft),
      rearRight: clampTirePercent(tires.rearRight),
    };
    let changed = false;
    for (const wheel of TIRE_ORDER) {
      const percent = displayed[wheel];
      if (this.displayedTires?.[wheel] === percent) continue;
      changed = true;
      const cell = this.tireCells[wheel];
      const value = this.tireValues[wheel];
      const level = tireLevel(percent);
      cell.className = `hud-tire hud-tire--${level}`;
      cell.setAttribute('aria-label', `${TIRE_LABELS[wheel]} tire ${percent}% (${level})`);
      value.textContent = `${percent}%`;
      const fill = cell.querySelector<HTMLElement>('.hud-tire-fill');
      if (fill) fill.style.setProperty('--tire-health', `${percent}%`);
      if (
        level !== this.announcedTireLevels[wheel] &&
        (level === 'critical' || level === 'depleted')
      ) {
        this.showStatusMessage(
          level === 'depleted'
            ? `${TIRE_LABELS[wheel]} tire depleted: control severely reduced`
            : `${TIRE_LABELS[wheel]} tire critical: cornering and steering reduced`,
        );
      }
      this.announcedTireLevels[wheel] = level;
    }
    if (changed) {
      this.displayedTires = {
        ...displayed,
      };
    }
  }

  setMinimapCarPoint(worldX: number, worldY: number): void {
    this.minimapCarPoint.style.left = `${worldX * this.minimapScale}px`;
    this.minimapCarPoint.style.top = `${worldY * this.minimapScale}px`;
  }

  showStatusMessage(text: string): void {
    this.enqueueMessage(text, '', 'status');
  }

  showErrorMessage(text: string): void {
    this.enqueueMessage(text, '', 'error');
  }

  showTimingMessage(text: string, tone: TimingMessageTone, accessibleText = text): void {
    this.enqueueMessage(text, `hud-status-message--${tone}`, 'timing', accessibleText);
  }

  setZoneStatus(
    pitSpeedLimitActive: boolean,
    pitServiceActive: boolean,
    pitServiceVisit = false,
  ): void {
    this.zoneStatus.className = 'hud-zone-status';
    const labels: string[] = [];
    if (pitSpeedLimitActive) labels.push('LIMITER');
    if (pitServiceActive) labels.push('SERVICE');
    this.zoneStatus.textContent = labels.length ? labels.join(' · ') : '';
    if (pitSpeedLimitActive) this.zoneStatus.classList.add('hud-zone-status--limiter');
    if (pitServiceActive) this.zoneStatus.classList.add('hud-zone-status--service');
    if (pitServiceVisit) this.showStatusMessage('Pit service complete — all tires restored');
  }

  private enqueueMessage(
    text: string,
    className: string,
    priority: HudMessagePriority,
    accessibleText = text,
  ): void {
    const message = { text, accessibleText, className, priority };
    const shouldPresent = this.messageQueue.enqueue(message);
    if (!shouldPresent) return;
    if (this.statusTimerId !== null) window.clearTimeout(this.statusTimerId);
    this.presentMessage(shouldPresent);
  }

  private presentMessage(message: {
    text: string;
    className: string;
    accessibleText: string;
  }): void {
    this.statusMessage.className = `hud-status-message ${message.className}`.trim();
    this.statusMessage.textContent = message.text;
    this.statusMessage.setAttribute('aria-label', message.accessibleText);
    this.statusMessage.classList.add('is-visible');
    this.statusTimerId = window.setTimeout(() => {
      this.statusMessage.classList.remove('is-visible');
      this.statusTimerId = null;
      const next = this.messageQueue.complete();
      if (next) this.presentMessage(next);
    }, 2600);
  }

  destroy(): void {
    if (this.statusTimerId !== null) {
      window.clearTimeout(this.statusTimerId);
      this.statusTimerId = null;
    }
    this.messageQueue.clear();
    this.root.remove();
  }

  private makeRow(label: string, value: HTMLSpanElement): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'hud-row';

    const key = document.createElement('span');
    key.className = 'hud-key';
    key.textContent = label;

    value.className = 'hud-value';

    row.append(key, value);
    return row;
  }

  private formatTime(timeMs: number): string {
    return timeMs > 0 ? (timeMs / 1000).toFixed(3) : NO_TIME;
  }
}

function tireLevel(percent: number): string {
  if (percent === 0) return 'depleted';
  if (percent <= 30) return 'critical';
  if (percent <= 60) return 'worn';
  return 'healthy';
}

function clampTirePercent(value: number): number {
  return Math.round(Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0);
}
